import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, CALENDAR_URL } from "@/lib/site";
import { PaperPlane } from "@/components/PaperPlane";

const Wordmark = () => (
  <span className="font-display text-xl sm:text-2xl tracking-tight text-white inline-flex items-center gap-2">
    <PaperPlane className="w-5 h-5 -rotate-12" />
    <span><span className="font-black">Prolific</span><span className="font-light text-cream/90"> Maven</span></span>
  </span>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled || open ? "backdrop-blur-xl bg-ink-900/95 border-b border-white/5" : "bg-transparent"}`}
    >
      <nav className="relative z-50 max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" aria-label="Prolific Maven home"><Wordmark /></Link>

        <div className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-underline text-sm tracking-wide text-cream/80 hover:text-white transition-colors ${isActive ? "active text-white" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={CALENDAR_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-apply-now"
            className="bg-gold text-ink-900 font-bold text-sm px-6 py-3 hover:bg-gold-light transition-colors"
          >
            Apply Now
          </a>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div
          className="lg:hidden fixed inset-x-0 top-20 bottom-0 bg-ink-900/98 z-40 flex flex-col justify-center items-center gap-8"
          data-testid="mobile-menu"
          onClick={() => setOpen(false)}
        >
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              className="font-display text-3xl font-bold text-cream hover:text-gold transition-colors"
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={CALENDAR_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-apply-now"
            className="mt-4 label text-xs bg-gold text-ink-900 px-10 py-4 rounded-[2px]"
          >
            Apply Now →
          </a>
          <span className="mt-6 label text-[10px] text-parch/60">Tap anywhere to close</span>
        </div>
      )}
    </header>
  );
};

export default Navbar;
